import { observer } from 'mobx-react-lite';
import { tokens, useTranslation } from '@shared/utils/i18n';
import { Box, Checkbox, FormControlLabel, LinearProgress } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
// GridRenderCellParams нужен, т.к. колонка 'collapse' теперь создаётся условным спредом внутри
// литерала массива (OSI-806) — вне контекста GridColDef тип params больше не выводится автоматически
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import {
  IOsiSystemReportsWidgetVmToken,
  IOsiSystemReportsWidgetViewModel
} from '@shared/types/mobx/widgets/OsiSystemReports';
import { useInjection } from 'inversify-react';
import { Trans } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { darken, lighten } from '@mui/material/styles';

const tMainPrefix = 'systemReports';

// Ячейка чекбокса «Свернуть дочерние помещения» вынесена в ОТДЕЛЬНЫЙ observer-компонент.
// Причина: DataGridPro v5 вызывает renderCell в своём внутреннем мемоизированном контексте,
// вне reactive-прохода родителя-observer. Если читать состояние чекбокса прямо в инлайн-JSX
// внутри renderCell — это чтение НЕ трекается MobX, и ячейка не перерисовывается при изменении
// observable (onChange срабатывает, значение в сторе меняется, но checked визуально не обновляется).
// Здесь же чтение vm.isCollapseFor(reportId) происходит ВНУТРИ рендера этого observer-компонента,
// поэтому подписка MobX работает и чекбокс перерисовывается корректно.
// Состояние чекбокса теперь PER-REPORT: компонент получает reportId и читает/пишет состояние
// именно своего отчёта (vm.isCollapseFor/vm.setCollapseFor), поэтому чекбоксы двух отчётов независимы.
const CollapseChildrenCell = observer(
  ({
    vm,
    reportId,
    label
  }: {
    vm: IOsiSystemReportsWidgetViewModel;
    reportId: number;
    label: string;
  }) => (
    <FormControlLabel
      // stopPropagation ТОЛЬКО на mousedown — этого достаточно: DataGridPro перехватывает
      // фокус ячейки именно на mousedown, и блокировка всплытия mousedown не даёт гриду
      // увести фокус с чекбокса. onChange чекбокса при этом всплывает отдельно и срабатывает.
      // onClick намеренно НЕ останавливаем: stopPropagation на <label> ломал нативный
      // форвардинг label↔input (двойное переключение → checked возвращался назад → чекбокс визуально не менялся).
      onMouseDown={(e) => e.stopPropagation()}
      control={
        <Checkbox
          checked={vm.isCollapseFor(reportId)}
          onChange={(e) => vm.setCollapseFor(reportId, e.target.checked)}
          disabled={vm.isLoading}
        />
      }
      label={label}
    />
  )
);

const getBackgroundColor = (color: string, mode: string) =>
  mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6);

const getHoverBackgroundColor = (color: string, mode: string) =>
  `${mode === 'dark' ? darken(color, 0.5) : lighten(color, 0.5)} !important`;

export const OsiSystemReportsWidget: React.FC = observer(() => {
  const vm = useInjection<IOsiSystemReportsWidgetViewModel>(IOsiSystemReportsWidgetVmToken);
  const { getGridLocale, t, translateToken: tt } = useTranslation();

  // OSI-806: признак наличия у ОСИ дочерних помещений (паркинги/кладовки) — читаем флаг ЗДЕСЬ,
  // в теле observer-компонента, а НЕ внутри renderCell. Причина та же, что и у CollapseChildrenCell выше:
  // DataGridPro вызывает renderCell вне reactive-прохода родителя, поэтому чтение observable внутри
  // renderCell не трекается MobX. Читая значение здесь, мы получаем подписку: при изменении флага
  // родитель перерисуется и массив columns пересоберётся уже с колонкой 'collapse' или без неё.
  const { hasChildAbonents } = vm;

  const columns: GridColDef[] = [
    {
      field: 'action',
      headerName: '',
      minWidth: 180,
      align: 'center',

      renderCell: (params) => (
        <LoadingButton
          loading={vm.isLoading}
          variant="outlined"
          color="primary"
          startIcon={<CloudDownloadIcon />}
          onClick={() => vm.onDownloadReportClick(params.row)}
          sx={{ m: 1 }}
          size={'medium'}
        >
          <Trans t={t} i18nKey={'common:download'} />
        </LoadingButton>
      )
    },
    {
      field: 'name',
      headerName: t(`${tMainPrefix}:debts.table.name`),
      valueFormatter: (params) => t(params.value),
      minWidth: 100,
      flex: 0.3,
      headerAlign: 'center'
    },
    {
      field: 'comment',
      headerName: t(`${tMainPrefix}:debts.table.comment`),
      valueFormatter: (params) => t(params.value),
      minWidth: 200,
      flex: 0.6,
      headerAlign: 'center'
    },
    // OSI-806: колонка с чекбоксом «Свернуть дочерние помещения» добавляется в массив columns
    // ТОЛЬКО когда у ОСИ реально есть дочерние помещения (паркинги/кладовки). В домах без них
    // колонка не создаётся вовсе — грид рисует три колонки, а оставшуюся ширину они
    // перераспределяют между собой по своим flex (name 0.3 / comment 0.6), пустого столбца не остаётся.
    // Значения collapseByReport при этом НАМЕРЕННО НЕ сбрасываем — сворачивать нечего,
    // и параметр collapseChildren в выгрузках отчётов ни на что не влияет.
    ...(hasChildAbonents
      ? [
          {
            field: 'collapse',
            headerName: '',
            minWidth: 240,
            sortable: false,
            headerAlign: 'center' as const,
            // Чекбокс показываем только для строк, у которых доступна опция (hasCollapseOption):
            // это отсекает отчёты без опции сворачивания. Признак наличия дочерних помещений
            // здесь уже не проверяем — он решает судьбу ВСЕЙ колонки (условие спреда выше).
            // Сам чекбокс рендерится через отдельный observer-компонент CollapseChildrenCell (см. выше) —
            // иначе DataGridPro не перерисовывает ячейку при изменении состояния.
            // reportId (params.row.id) пробрасываем пропсом — состояние чекбокса per-report.
            // label пробрасываем готовой строкой пропсом (перевод берёт родитель через useTranslation).
            renderCell: (params: GridRenderCellParams) =>
              params.row.hasCollapseOption ? (
                <CollapseChildrenCell vm={vm} reportId={params.row.id} label={tt(tokens.osiOSV.collapseChildren)} />
              ) : null
          }
        ]
      : [])
  ];

  return (
    <Box
      sx={{
        height: '60vh',
        '& .row-child--level-1': {
          bgcolor: (theme) => getBackgroundColor(theme.palette.info.main, theme.palette.mode),
          '&:hover': {
            bgcolor: (theme) => getHoverBackgroundColor(theme.palette.info.main, theme.palette.mode)
          }
        }
      }}
    >
      <DataGridPro
        sx={{
          '& .MuiDataGrid-row': {
            '&.Mui-selected': {
              backgroundColor: 'unset',
              '&:hover': {
                backgroundColor: 'unset'
              }
            },
            '&:hover': {
              backgroundColor: 'unset'
            }
          },
          '& .MuiDataGrid-cell': {
            '&:focus-within': {
              outline: 'none !important'
            }
          }
        }}
        loading={vm.isLoading}
        components={{
          LoadingOverlay: LinearProgress
        }}
        localeText={getGridLocale()}
        getRowHeight={() => 'auto'}
        getRowClassName={(params) => `row-child--level-${params.getValue(params.id, 'level')}`}
        rows={vm.reports}
        columns={columns}
        density="compact"
        disableSelectionOnClick
        hideFooter
      />
    </Box>
  );
});
